import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {HiInformationCircle} from 'react-icons/hi';
import {useDispatch, useSelector} from 'react-redux';
import {setSlackWizardSelectionMethod, setSlackWizardWorkUnitTypeFilter} from '../../../../../../redux/slices/settings';
import SelectedChips from './SelectedChips';
import InfiniteScrollDropdown from "../../../../../../components/common/InfiniteScrollDropdown";
import {WizardOption} from "../../../../types";

type WorkUnitType = 'all' | 'roadmap' | 'project';

interface WorkUnitSelectionProps {
  options: WizardOption[];
  selectedIds: string[];
  labelsMap?: Record<string, string>;
  onAdd: (option: WizardOption) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSearch: (term: string, searchType?: WorkUnitType) => void;
}

const WorkUnitSelection: React.FC<WorkUnitSelectionProps> = ({
  options,
  selectedIds,
  labelsMap,
  onAdd,
  onRemove,
  onClear,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onSearch
}) => {
  const dispatch = useDispatch();
  const { 
    slackWizardSelectionMethod: selectionMethod,
    slackWizardWorkUnitTypeFilter: workUnitType = 'all'
  } = useSelector((state: any) => state.settings || {});
  const [pendingActiveSelection, setPendingActiveSelection] = useState<{ limit: number } | null>(null);
  const [previousSelectiveIds, setPreviousSelectiveIds] = useState<string[] | null>(null);

  // Type filter options for animated pill buttons
  const typeOptions = useMemo(() => [
    { value: 'all' as WorkUnitType, label: 'All Types' },
    { value: 'roadmap' as WorkUnitType, label: 'Epics' },
    { value: 'project' as WorkUnitType, label: 'Features' },
  ], []);

  // Dynamic labels based on selected work item type
  const typeLabels = useMemo(() => {
    switch (workUnitType) {
      case 'roadmap':
        return {
          singular: 'Epic',
          plural: 'Epics',
          activeLabel: 'Active Epics',
          selectiveLabel: 'Pick Specific Epics',
          searchPlaceholder: 'Search and select epics...',
          dropdownLabel: 'Epics',
        };
      case 'project':
        return {
          singular: 'Feature',
          plural: 'Features',
          activeLabel: 'Active Features',
          selectiveLabel: 'Pick Specific Features',
          searchPlaceholder: 'Search and select features...',
          dropdownLabel: 'Features',
        };
      default:
        return {
          singular: 'Work Item',
          plural: 'Work Items',
          activeLabel: 'Active Work Items',
          selectiveLabel: 'Pick Specific Work Items',
          searchPlaceholder: 'Search and select work items...',
          dropdownLabel: 'Work Items',
        };
    }
  }, [workUnitType]);

  // Refs for animated type indicator
  const typeButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const [typeIndicatorStyle, setTypeIndicatorStyle] = useState({ width: 0, left: 0 });

  const filteredOptions = useMemo(() => options.filter((opt) => !selectedIds.includes(opt.value)), [options, selectedIds]);

  const getLabel = (id: string) => (labelsMap?.[id] || options.find((o) => o.value === id)?.label || id);
  
  // Show selected IDs that have a label either in labelsMap or in current options
  const visibleSelectedIds = useMemo(() => {
    return selectedIds.filter((id) => {
      // Check if label exists in labelsMap
      if (labelsMap?.[id]) return true;
      // Check if label can be found in options
      const optionLabel = options.find((o) => o.value === id)?.label;
      return Boolean(optionLabel);
    });
  }, [selectedIds, labelsMap, options]);

  const handleEntitySelect = (value: string) => {
    if (!value) return;
    // Find the full option data and pass it to onAdd
    const option = options.find((opt) => opt.value === value);
    if (option) {
      onAdd(option);
    }
  };

  // Update type indicator position
  const updateTypeIndicator = useCallback(() => {
    const activeButton = typeButtonRefs.current[workUnitType];
    if (activeButton) {
      setTypeIndicatorStyle({
        width: activeButton.offsetWidth,
        left: activeButton.offsetLeft
      });
    }
  }, [workUnitType]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      updateTypeIndicator();
    });
    return () => cancelAnimationFrame(frame);
  }, [workUnitType, updateTypeIndicator]);

  useEffect(() => {
    window.addEventListener('resize', updateTypeIndicator);
    return () => window.removeEventListener('resize', updateTypeIndicator);
  }, [updateTypeIndicator]);

  const handleTypeClick = useCallback((newType: WorkUnitType) => {
    dispatch(setSlackWizardWorkUnitTypeFilter(newType));
    // Clear selected items when changing type
    // onClear();
    // Trigger search with the new type
    onSearch('', newType);
  }, [dispatch, onSearch, onClear]);

  useEffect(() => {
    if (!pendingActiveSelection) return;
    const { limit } = pendingActiveSelection;

    const current = selectedIds;
    const allowed = Math.max(0, limit - current.length);
    if (allowed <= 0) {
      setPendingActiveSelection(null);
      return;
    }

    const activeOptions = options.filter((opt) => opt.isActive !== false);
    const newSelections = activeOptions
      .filter((opt) => !current.includes(opt.value))
      .slice(0, allowed);

    if (newSelections.length > 0) {
      newSelections.forEach((opt) => onAdd(opt));
      setPendingActiveSelection(null);
      return;
    }

    if (hasMore && !loadingMore) {
      onLoadMore();
      return;
    }

    setPendingActiveSelection(null);
  }, [pendingActiveSelection, options, selectedIds, hasMore, loadingMore, onLoadMore, onAdd]);

  // Default selection method based on existing selection
  useEffect(() => {
    if (selectionMethod === null) {
      const method = (selectedIds?.length || 0) > 0 ? 'selective' : 'active';
      try { dispatch(setSlackWizardSelectionMethod(method as any)); } catch (e) {}
    }
  }, [selectionMethod, selectedIds, dispatch]);

  return (
    <div className="space-y-4">
      {/* Descriptive toggle to choose All vs Specific */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
        {/* Type Filter Pills - Always visible at the top */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Work Item Type
          </label>
          <div className="relative inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 h-[38px]">
            {/* Animated indicator */}
            <div
              className="absolute h-full bg-primary-light dark:bg-primary-dark rounded-lg transition-all duration-300 ease-in-out shadow-sm"
              style={{
                width: typeIndicatorStyle.width,
                left: typeIndicatorStyle.left,
                zIndex: 1
              }}
            />
            
            {typeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                ref={(el) => { typeButtonRefs.current[option.value] = el; }}
                onClick={() => handleTypeClick(option.value)}
                className={`relative px-4 h-full text-sm font-medium transition-colors duration-300 rounded-lg ${
                  workUnitType === option.value
                    ? 'text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
                style={{ zIndex: 2 }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scope Selection */}
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Scope
          </label>
        </div>
        <div className="flex items-center gap-6">
          <label className="inline-flex items-center gap-2 cursor-pointer min-w-[160px]">
            <input
              type="radio"
              name="wu-scope"
              className="w-4 h-4 text-primary-light flex-shrink-0"
              checked={selectionMethod === 'active'}
              onChange={() => {
                if (selectionMethod !== 'active') {
                  // Preserve current selective selection before clearing
                  setPreviousSelectiveIds(selectedIds);
                  try { dispatch(setSlackWizardSelectionMethod('active')); } catch (e) {}
                  onClear();
                  setPendingActiveSelection({ limit: 30 });
                }
              }}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{typeLabels.activeLabel}</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer min-w-[180px]">
            <input
              type="radio"
              name="wu-scope"
              className="w-4 h-4 text-primary-light flex-shrink-0"
              checked={selectionMethod === 'selective'}
              onChange={() => {
                if (selectionMethod !== 'selective') {
                  try { dispatch(setSlackWizardSelectionMethod('selective')); } catch (e) {}
                  // Clear active auto-selection and restore previously chosen selective ids (if any)
                  onClear();
                  if (previousSelectiveIds?.length) {
                    // Find the full option data for each previously selected ID
                    previousSelectiveIds.forEach((id) => {
                      const opt = options.find((o) => o.value === id);
                      if (opt) {
                        onAdd(opt);
                      }
                    });
                  }
                  setPendingActiveSelection(null);
                }
              }}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{typeLabels.selectiveLabel}</span>
          </label>
        </div>

        {selectionMethod === 'active' && (
          <div className="mt-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-700 dark:text-blue-300">
            <div className="flex items-start gap-2">
              <HiInformationCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-1.5 flex-1">
                <p>
                  Active {typeLabels.plural.toLowerCase() == 'work items' ? 'features and epics' : typeLabels.plural.toLowerCase()} are those whose status is not <span className="font-medium">closed</span>.
                </p>
                <p className="text-blue-600 dark:text-blue-400">
                  A single consolidated report will be generated for the 30 most recent active {typeLabels.plural.toLowerCase() == 'work items' ? 'features and epics' : typeLabels.plural.toLowerCase()} at the time of trigger and sent to your selected channel.
                </p>
              </div>
            </div>
          </div>
        )}

        {selectionMethod === 'selective' && (
          <>
            <div className="mt-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-700 dark:text-blue-300">
              <div className="flex items-start gap-2">
                <HiInformationCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p>
                  A single consolidated report will be generated for all selected {typeLabels.plural.toLowerCase()} and sent to your selected channel.
                </p>
              </div>
            </div>

            <div className="mt-4">
              <InfiniteScrollDropdown
                id="work-item-picker"
                name="work-item-picker"
                label={typeLabels.dropdownLabel}
                value=""
                placeholder={typeLabels.searchPlaceholder}
                onChange={handleEntitySelect}
                options={filteredOptions}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                onLoadMore={onLoadMore}
                onSearch={(term) => onSearch(term, workUnitType)}
                required={true}
                showAsterisk={true}
              />
            </div>
          </>
        )}
      </div>

      {selectionMethod === 'selective' && !!visibleSelectedIds.length && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex items-center gap-2 p-4 pb-3">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">
              {visibleSelectedIds.length} {visibleSelectedIds.length > 1 ? "work items" : "work item"} Selected
            </h4>
          </div>
          <SelectedChips ids={visibleSelectedIds} getLabel={getLabel} onRemove={onRemove} scrollable={selectionMethod === 'selective'} />
        </div>
      )}
    </div>
  );
};

export default WorkUnitSelection;